import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { updateDoc, doc } from 'firebase/firestore';
import QuestionEditor from '../questionBank/QuestionEditor';
import '../tests/TestEditor.css'; // Reusing existing modal styles

const LessonTestEditor = ({ test, onClose }) => {
  const [currentQuestions, setCurrentQuestions] = useState(test.questions || []);
  const [showQuestionEditor, setShowQuestionEditor] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const [lessonUrl, setLessonUrl] = useState(test.lessonUrl || '');

  const totalInPool = currentQuestions.length;
  const willDisplay = Math.min(totalInPool, test.maxQuestions);

  // Count questions by test type
  const countByTestType = () => {
    const counts = { vocab: 0, grammar: 0, comprehension: 0 };
    currentQuestions.forEach(q => {
      if (counts.hasOwnProperty(q.testType)) {
        counts[q.testType]++;
      }
    });
    return counts;
  };

  const testTypeCounts = countByTestType();

  const handleAddQuestion = () => {
    setEditingQuestion(null);
    setEditingIndex(null);
    setShowQuestionEditor(true);
  };

  const handleEditQuestion = (question, index) => {
    setEditingQuestion(question);
    setEditingIndex(index);
    setShowQuestionEditor(true);
  };

  const handleRemoveQuestion = (index) => {
    if (!window.confirm('Are you sure you want to remove this question?')) {
      return;
    }
    setCurrentQuestions(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveQuestion = (questionData) => {
    // Add metadata that matches global question bank structure
    const fullQuestionData = {
      ...questionData,
      levelId: test.levelId,
      id: editingIndex !== null ? currentQuestions[editingIndex].id : `lq_${Date.now()}`,
      createdAt: editingIndex !== null ? currentQuestions[editingIndex].createdAt : new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    if (editingIndex !== null) {
      // Update existing question
      const newQuestions = [...currentQuestions];
      newQuestions[editingIndex] = fullQuestionData;
      setCurrentQuestions(newQuestions);
    } else {
      // Add new question
      setCurrentQuestions(prev => [...prev, fullQuestionData]);
    }

    setShowQuestionEditor(false);
    setEditingQuestion(null);
    setEditingIndex(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateDoc(doc(db, 'lessonTests', test.id), {
        questions: currentQuestions,
        lessonUrl: lessonUrl.trim(),
        updatedAt: new Date().toISOString()
      });
      alert('Changes saved successfully!');
      onClose();
    } catch (error) {
      console.error('Error saving changes:', error);
      alert('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const getQuestionPreview = (question) => {
    const content = question.content?.stem || question.content?.text || question.content?.instructions || '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    const text = tempDiv.textContent || tempDiv.innerText || '';
    return text.substring(0, 100) + (text.length > 100 ? '...' : '');
  };

  return (
    <div className="test-editor-overlay">
      <div className="test-editor-modal">
        <div className="editor-header">
          <div style={{flex: 1}}>
            <h2>{test.lessonTitle}</h2>
            {test.lessonUrl ? (
              <p className="test-meta" style={{marginBottom: '0.25rem'}}>
                🔗 <a href={test.lessonUrl} target="_blank" rel="noopener noreferrer" style={{color: '#2196F3', textDecoration: 'none'}}>
                  View Lesson
                </a>
              </p>
            ) : (
              <p className="test-meta" style={{marginBottom: '0.25rem', color: '#ff9800'}}>
                ⚠️ No lesson URL set
              </p>
            )}
            <p className="test-meta">
              {test.levelId.replace('-', ' ').toUpperCase()} | 
              Pool: {totalInPool} questions | 
              Displays: {willDisplay} random
            </p>
          </div>
          <button className="close-icon-btn" onClick={onClose}>×</button>
        </div>

        <div className="editor-content">
          {/* Lesson URL Section */}
          {!test.lessonUrl && (
            <div className="info-box" style={{marginBottom: '1.5rem', background: '#fff3cd', borderLeftColor: '#ffc107'}}>
              <p style={{margin: 0, marginBottom: '0.5rem', color: '#856404'}}>
                <strong>⚠️ No lesson URL set</strong>
              </p>
              <p style={{margin: 0, marginBottom: '0.75rem', fontSize: '0.9rem', color: '#856404'}}>
                Add the lesson URL so students can practice after taking the test.
              </p>
              <input
                type="url"
                placeholder="https://learn.litvill.com/learn/lesson/..."
                value={lessonUrl}
                onChange={(e) => setLessonUrl(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '0.9rem'
                }}
              />
            </div>
          )}

          {/* Current Questions Section */}
          <div className="current-questions-section">
            <div className="section-header">
              <h3>Question Pool ({totalInPool} questions)</h3>
              <button 
                onClick={handleAddQuestion}
                className="add-questions-btn"
              >
                + Create Question
              </button>
            </div>

            {totalInPool > 0 && (
              <div className="info-box" style={{marginBottom: '1rem'}}>
                <p style={{margin: 0, marginBottom: '0.5rem'}}>
                  {totalInPool < test.maxQuestions ? (
                    <>ℹ️ Pool has {totalInPool} questions. All {totalInPool} will be displayed. Add {test.maxQuestions - totalInPool} more to enable randomization.</>
                  ) : totalInPool === test.maxQuestions ? (
                    <>✓ Pool has {totalInPool} questions matching the display limit. Add more questions for variety through randomization!</>
                  ) : (
                    <>✓ Pool has {totalInPool} questions. Test will randomly display {test.maxQuestions} each time.</>
                  )}
                </p>
                <p style={{margin: 0, fontSize: '0.9rem', color: '#666'}}>
                  <strong>Mix:</strong> {testTypeCounts.vocab} vocab, {testTypeCounts.grammar} grammar, {testTypeCounts.comprehension} comprehension
                </p>
              </div>
            )}

            {currentQuestions.length === 0 ? (
              <div className="empty-message">
                No questions created yet. Click "Create Question" to add your first question.
              </div>
            ) : (
              <div className="questions-list">
                {currentQuestions.map((question, index) => (
                  <div key={question.id || index} className="question-item">
                    <div className="question-number">{index + 1}</div>
                    <div className="question-info">
                      <div className="question-header">
                        <span className="question-type-badge">
                          {question.questionType}
                        </span>
                        <span className="question-test-type">
                          {question.testType}
                        </span>
                      </div>
                      <div className="question-preview">
                        {getQuestionPreview(question)}
                      </div>
                    </div>
                    <button
                      onClick={() => handleEditQuestion(question, index)}
                      className="edit-btn"
                      style={{marginRight: '0.5rem', minWidth: '60px', padding: '0.5rem'}}
                      title="Edit question"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleRemoveQuestion(index)}
                      className="remove-btn"
                      title="Remove question"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="editor-footer">
          <button onClick={onClose} className="cancel-footer-btn">
            Cancel
          </button>
          <button 
            onClick={handleSave} 
            className="save-btn"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Question Editor Modal - level pre-filled from lesson test */}
      {showQuestionEditor && (
        <QuestionEditor
          question={editingQuestion}
          onSave={handleSaveQuestion}
          onCancel={() => {
            setShowQuestionEditor(false);
            setEditingQuestion(null);
            setEditingIndex(null);
          }}
          defaultLevelId={test.levelId}
          hideLevelField={true}
        />
      )}
    </div>
  );
};

export default LessonTestEditor;